import type { EventLocationType } from '@/lib/types/events.types';

/** Human-readable label for an event location type. */
export function locationLabel(locationType: EventLocationType | string | null): string {
  if (locationType === 'virtual') return 'Virtual';
  if (locationType === 'in_person') return 'In Person';
  return 'TBD';
}

const FEATURED_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
  timeZoneName: 'short',
});

const CARD_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

/** "Tue, 19 Aug 2026 · 11:59 AM GMT+5" — used on the featured event card. */
export function formatFeaturedDateLabel(isoString: string): string {
  try {
    const date = new Date(isoString);
    const parts = Object.fromEntries(
      FEATURED_DATE_FORMATTER.formatToParts(date).map(({ type, value }) => [type, value]),
    );
    const period = parts.dayPeriod ? ` ${parts.dayPeriod}` : '';
    const tz = parts.timeZoneName ? ` ${parts.timeZoneName}` : '';
    return `${parts.weekday}, ${parts.day} ${parts.month} ${parts.year} · ${parts.hour}:${parts.minute}${period}${tz}`.trim();
  } catch {
    return new Date(isoString).toLocaleString();
  }
}

/**
 * "Tue 24 Jun" — compact date used on event cards. The year is shown only when
 * it differs from `now`'s, so the card's date line fits on one row (UX sheet v2).
 */
export function formatCardDateLabel(isoString: string, now: Date = new Date()): string {
  try {
    const date = new Date(isoString);
    const parts = Object.fromEntries(
      CARD_DATE_FORMATTER.formatToParts(date).map(({ type, value }) => [type, value]),
    );
    const year = date.getFullYear() === now.getFullYear() ? '' : ` ${parts.year}`;
    return `${parts.weekday} ${parts.day} ${parts.month}${year}`.trim();
  } catch {
    return new Date(isoString).toLocaleDateString();
  }
}

const CARD_TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

const TIME_ZONE_FORMATTER = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' });

/** Short zone name in the viewer's time zone, e.g. "GMT+5" (empty if the runtime omits it). */
function timeZoneLabel(date: Date): string {
  return TIME_ZONE_FORMATTER.formatToParts(date).find((part) => part.type === 'timeZoneName')?.value ?? '';
}

/** Local calendar day key, so "same day" follows the viewer's clock, not UTC. */
function localDayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

/**
 * Date plus start and end time for the dashboard event widgets (UX sheet v2),
 * in the viewer's time zone like `formatCardDateLabel`:
 * "Tue 15 Sep · 5:00 PM - 7:00 PM GMT+5" (year only when not `now`'s). An end
 * on a later day repeats its date so the range cannot be misread; no end shows
 * the start time only.
 */
export function formatCardDateTimeRange(
  startsAt: string,
  endsAt: string | null,
  now: Date = new Date(),
): string {
  const start = new Date(startsAt);
  const zone = timeZoneLabel(start);
  const startLabel = `${formatCardDateLabel(startsAt, now)} · ${CARD_TIME_FORMATTER.format(start)}`;
  if (!endsAt) return `${startLabel} ${zone}`.trim();

  const end = new Date(endsAt);
  const endTime = CARD_TIME_FORMATTER.format(end);
  const endLabel =
    localDayKey(start) === localDayKey(end) ? endTime : `${formatCardDateLabel(endsAt, now)} ${endTime}`;
  return `${startLabel} - ${endLabel} ${zone}`.trim();
}

/**
 * Time-only range for the event detail page's Time tile, which sits beside a
 * separate Date tile: "10:00 PM - 1:00 AM GMT+5". `endsOn` carries the end's
 * date ("Thu 22 Oct") only when the event ends on a later local day, so an
 * overnight range is never misread; null otherwise.
 */
export function formatEventTimeRange(
  startsAt: string,
  endsAt: string | null,
  now: Date = new Date(),
): { time: string; endsOn: string | null } {
  const start = new Date(startsAt);
  const zone = timeZoneLabel(start);
  const startTime = CARD_TIME_FORMATTER.format(start);
  if (!endsAt) return { time: `${startTime} ${zone}`.trim(), endsOn: null };

  const end = new Date(endsAt);
  return {
    time: `${startTime} - ${CARD_TIME_FORMATTER.format(end)} ${zone}`.trim(),
    endsOn: localDayKey(start) === localDayKey(end) ? null : formatCardDateLabel(endsAt, now),
  };
}
